import { useGetList, useTimeout } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";

import type { Contact, ContactNote } from "../types";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardError } from "./Dashboard";
import { DashboardMetrics } from "./DashboardMetrics";
import { DashboardStepper } from "./DashboardStepper";
import { HotContacts } from "./HotContacts";
import { TasksList } from "./TasksList";
import { Welcome } from "./Welcome";
import { MobileContent } from "../layout/MobileContent";

const FADE_UP = (delay: number) => `fadeUpIn 0.5s ease-out ${delay}s both`;

const Section = ({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) => <div style={{ animation: FADE_UP(delay) }}>{children}</div>;

const Divider = () => (
  <div style={{ borderTop: "1px solid var(--nd-border)" }} />
);

const MobileSkeleton = () => (
  <div className="flex flex-col animate-pulse">
    <div className="flex py-4">
      <div className="flex flex-col gap-2 pr-5">
        <Skeleton className="h-9 w-20 rounded-none" />
        <Skeleton className="h-2 w-12 rounded-none" />
      </div>
      <div
        className="flex flex-col gap-2 px-5"
        style={{ borderLeft: "1px solid var(--nd-border)" }}
      >
        <Skeleton className="h-9 w-12 rounded-none" />
        <Skeleton className="h-2 w-16 rounded-none" />
      </div>
      <div
        className="flex flex-col gap-2 px-5"
        style={{ borderLeft: "1px solid var(--nd-border)" }}
      >
        <Skeleton className="h-9 w-12 rounded-none" />
        <Skeleton className="h-2 w-12 rounded-none" />
      </div>
    </div>
    <Divider />
    <div className="py-6 flex flex-col gap-3">
      <Skeleton className="h-2 w-16 rounded-none" />
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-12 w-full rounded-xl" />
      ))}
    </div>
    <Divider />
    <div className="py-6 flex flex-col gap-3">
      <Skeleton className="h-2 w-20 rounded-none" />
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-10 w-full rounded-xl" />
      ))}
    </div>
  </div>
);

export const MobileDashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
    isError: isErrorContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });

  const {
    total: totalContactNotes,
    isPending: isPendingContactNotes,
    isError: isErrorNotes,
  } = useGetList<ContactNote>("contact_notes", {
    pagination: { page: 1, perPage: 1 },
  });

  const oneSecondHasPassed = useTimeout(1000);

  const isPending = isPendingContact || isPendingContactNotes;

  if (isPending) {
    return oneSecondHasPassed ? (
      <MobileContent>
        <MobileSkeleton />
      </MobileContent>
    ) : null;
  }

  // Show error rather than the wrong stepper step when initial data fetches fail
  if (isErrorContact || isErrorNotes) {
    return (
      <MobileContent>
        <DashboardError />
      </MobileContent>
    );
  }

  if (!totalContact) {
    return (
      <MobileContent>
        <DashboardStepper step={1} />
      </MobileContent>
    );
  }

  if (!totalContactNotes) {
    return (
      <MobileContent>
        <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />
      </MobileContent>
    );
  }

  return (
    <MobileContent>
      {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}

      <Section delay={0}>
        <DashboardMetrics totalContacts={totalContact ?? 0} compact />
      </Section>

      <Divider />

      <Section delay={0.08}>
        <div className="py-6">
          <TasksList collapsible />
        </div>
      </Section>

      <Divider />

      <Section delay={0.16}>
        <div className="py-6">
          <DashboardActivityLog collapsible />
        </div>
      </Section>

      <Divider />

      <Section delay={0.24}>
        <div className="py-6">
          <HotContacts collapsible />
        </div>
      </Section>
    </MobileContent>
  );
};
